import React from "react";
import { format } from "date-fns";
import { isEmpty } from "lodash";
import { EntityId } from "@reduxjs/toolkit";
import { css } from "@emotion/react";
import { ImCheckmark } from "react-icons/im";
import { useTranslation } from "react-i18next";
import Button from "@components/base/button";
import theme from "@styles/theme";
import useAppSelector from "@hooks/useAppSelector";
import { selectSpaceById } from "@store/space/spaceSelectors";
import { selectUserById } from "@store/user/userSelectors";
import { getLanguage } from "@helpers";
import { Issue, WorkLog } from "@store/issue/types";
import IssueAttachments from "./attachments/IssueAttachments";
import Label from "./Label";
import IssueWorkLogRow from "./IssueWorkLogRow";

interface IssueDetailsProps {
  issue: Issue;
  onEditPressed: () => void;
}

const IssueDetails: React.FC<IssueDetailsProps> = ({
  issue: {
    attachments,
    description,
    spaceId,
    label,
    priority,
    state,
    workLogs,
    createdAt,
    reportedBy,
    authorId,
  },
  onEditPressed,
}) => {
  const { t } = useTranslation();
  const space = useAppSelector(({ space }) => selectSpaceById(space, spaceId));

  const filterWorkLogs = ({ nextState, prevState }: WorkLog) =>
    !isEmpty(nextState) && !isEmpty(prevState);

  const user = useAppSelector(({ user }) =>
    selectUserById(user, authorId as EntityId),
  );

  const filteredLogs = workLogs?.filter(filterWorkLogs) ?? [];

  return (
    <div css={styles.container}>
      <div css={styles.panelsContainer}>
        <div css={styles.panel}>
          <Label label={t("common.property")} value={space?.label} />
          <Label label={t("common.issue")} value={label} />
          <Label label={t("common.remark")} value={description} />
          {user && (
            <Label label={t("issue.createdBy")} value={user.profile.name} />
          )}
          {reportedBy && (
            <Label label={t("issue.reportedBy")} value={reportedBy} />
          )}
          {createdAt && (
            <Label
              label={t("issue.createdAt")}
              value={format(new Date(createdAt), "HH:mm, dd-MM-yyyy", {
                locale: getLanguage(),
              })}
            />
          )}
          {priority && (
            <div css={styles.priority}>
              <ImCheckmark css={styles.check} />
              <span>{t("issue.priority")}</span>
            </div>
          )}
        </div>

        <div css={styles.divider} />

        <div data-test-id="button-wrapper" css={styles.panel}>
          <IssueAttachments attachments={attachments} />

          <Label
            label={t("issue.states.title")}
            value={t(`issue.states.${state}`)}
          />

          {filteredLogs.length > 0 && (
            <div css={styles.logContainer}>
              <span>{t("issue.changeLog")}</span>
              {filteredLogs.map((workLog, index) => (
                <IssueWorkLogRow key={index} workLog={workLog} />
              ))}
            </div>
          )}

          <Button kind="tertiary" onClick={onEditPressed}>
            {t("issue.changeDetails")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: css({
    backgroundColor: theme.colors.white,
    width: 732,
  }),
  panelsContainer: css({
    display: "flex",
    marginTop: theme.sizing.scale1600,
  }),
  divider: css({
    display: "flex",
    width: 1,
    backgroundColor: theme.colors.mono200,
    margin: `0 ${theme.sizing.scale800}`,
  }),
  panel: css({
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flex: 1,
  }),
  check: css({
    marginRight: theme.sizing.scale400,
    fontSize: 24,
    color: theme.colors.positive,
  }),
  priority: css({
    display: "flex",
    alignItems: "center",
  }),
  logContainer: css({
    marginBottom: theme.sizing.scale1600,
  }),
};

export default IssueDetails;
